/**
 * sessionStorage drafts for the catalogue editor.
 *
 * Unsaved editor text is kept in sessionStorage, keyed by account and then
 * category (and item/form), so navigating away and back does not lose it and one
 * account never sees another's. It is sessionStorage, not localStorage: closing
 * the tab clears the work. A restored draft is only ever shown with the
 * "unsaved" chip - the saved row is written by the operator's own Save, so a row
 * changed elsewhere is never silently overwritten by a restore.
 *
 * The edit drafts (item, category) and the create drafts (new item, new
 * category) share the same prefix, so the audit index in PRODUCT_BACKLOG.md
 * covers every key this feature owns.
 */

import type { AdminCategory, AdminItem } from '../../api/client';
import { dollarsInput } from '../../shared/functions/formatter';

export type ItemDraft = {
  title: string;
  /** Kept as the operator's raw text so partial input is not fought with. */
  price: string;
  imageId: number | null;
  imageUrl: string | null;
};

export type CategoryDraft = {
  name: string;
  palette1: string;
  palette2: string;
  palette3: string;
};

export type NewItemDraft = {
  title: string;
  price: string;
  hidden: boolean;
};

export const EMPTY_NEW_ITEM: NewItemDraft = { title: '', price: '', hidden: false };

const DRAFT_PREFIX = 'cashier.cmsDraft';

export function itemDraftKey(scope: string, categoryId: number, itemId: number): string {
  return DRAFT_PREFIX + '.' + scope + '.' + categoryId + '.item.' + itemId;
}

export function categoryDraftKey(scope: string, categoryId: number): string {
  return DRAFT_PREFIX + '.' + scope + '.' + categoryId;
}

export function newItemDraftKey(scope: string, categoryId: number): string {
  return DRAFT_PREFIX + '.' + scope + '.' + categoryId + '.newItem';
}

export function newCategoryDraftKey(scope: string): string {
  return DRAFT_PREFIX + '.' + scope + '.newCategory';
}

/** Is any draft stored for this account? Unsaved work beats a fresh re-read. */
export function hasStoredDrafts(scope: string): boolean {
  try {
    const prefix = DRAFT_PREFIX + '.' + scope + '.';
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key !== null && key.startsWith(prefix)) return true;
    }
  } catch {
    // Unreadable storage: treat it as no drafts; the re-read is still safe.
  }
  return false;
}

export function readDraft<T>(key: string, isValid: (value: unknown) => value is T): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValid(parsed) ? parsed : null;
  } catch {
    // Unreadable storage or malformed JSON: behave as if there were no draft.
    return null;
  }
}

export function writeDraft(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A full or unavailable store must not break the editor; the text simply
    // will not survive a navigation.
  }
}

export function clearDraft(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to clean up.
  }
}

/** "12.5" -> 1250 cents. Returns null when the text is not a usable price. */
export function parsePriceToCents(value: string): number | null {
  const normalised = value.trim();
  if (!/^\d+(\.\d{0,2})?$/.test(normalised)) return null;
  return Math.round(Number(normalised) * 100);
}

export function toDraft(item: AdminItem): ItemDraft {
  return {
    title: item.title,
    price: dollarsInput(item.price_cents),
    imageId: item.image_id,
    imageUrl: item.image_url,
  };
}

export function isItemDraft(value: unknown): value is ItemDraft {
  if (!value || typeof value !== 'object') return false;
  const draft = value as Partial<ItemDraft>;
  return (
    typeof draft.title === 'string' &&
    typeof draft.price === 'string' &&
    (draft.imageId === null || typeof draft.imageId === 'number') &&
    (draft.imageUrl === null || typeof draft.imageUrl === 'string')
  );
}

export function isCategoryDraft(value: unknown): value is CategoryDraft {
  if (!value || typeof value !== 'object') return false;
  const draft = value as Partial<CategoryDraft>;
  return (
    typeof draft.name === 'string' &&
    typeof draft.palette1 === 'string' &&
    typeof draft.palette2 === 'string' &&
    typeof draft.palette3 === 'string'
  );
}

export function isNewItemDraft(value: unknown): value is NewItemDraft {
  if (!value || typeof value !== 'object') return false;
  const draft = value as Partial<NewItemDraft>;
  return typeof draft.title === 'string' && typeof draft.price === 'string' && typeof draft.hidden === 'boolean';
}

export function isNewCategoryDraft(value: unknown): value is string {
  return typeof value === 'string';
}

/** A draft differs from the row the server last sent. */
export function itemDraftDirty(item: AdminItem, draft: ItemDraft): boolean {
  return draft.title !== item.title || parsePriceToCents(draft.price) !== item.price_cents || draft.imageId !== item.image_id;
}

/** A create form holds something worth keeping. */
export function newItemDraftDirty(draft: NewItemDraft): boolean {
  return draft.title.trim() !== '' || draft.price.trim() !== '' || draft.hidden;
}

/** A new category name worth keeping. */
export function newCategoryDraftDirty(name: string | null): boolean {
  return (name ?? '').trim() !== '';
}

/** The stored drafts worth restoring: present, valid, and still different. */
export function restoreItemDrafts(scope: string, category: AdminCategory): Record<number, ItemDraft> {
  const restored: Record<number, ItemDraft> = {};
  for (const item of category.items) {
    const stored = readDraft(itemDraftKey(scope, category.id, item.id), isItemDraft);
    if (stored && itemDraftDirty(item, stored)) restored[item.id] = stored;
  }
  return restored;
}

/** Warn on a real reload or tab close, never on in-app navigation. */
export function warnBeforeUnload(dirty: boolean): () => void {
  if (!dirty) return () => {};
  const warn = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = '';
  };
  window.addEventListener('beforeunload', warn);
  return () => window.removeEventListener('beforeunload', warn);
}
